import { notEmpty } from "@product/scmp-sdk";
import classnames from "classnames";
import type { FunctionComponent } from "react";
import { useMemo } from "react";
import { graphql, useFragment } from "react-relay";

import { useLiveArticleScrollContext } from "~/components/article/article-render/live-article/contexts";
import { getTimelineItemClassName } from "~/components/article/article-render/live-article/helpers";
import { sendGA4Tracking } from "~/components/tracking/google-analytics-4/apis";
import { formatInTimeZone } from "~/lib/utils";
import type { itemLiveArticleTimelineLiveContent$key } from "~/queries/__generated__/itemLiveArticleTimelineLiveContent.graphql";

import { Container, Dot, Time, Title } from "./styles";

type Props = {
  className?: string;
  reference: itemLiveArticleTimelineLiveContent$key;
};

export const LiveArticleTimelineItem: FunctionComponent<Props> = ({ className, reference }) => {
  const { scrollToTimelineItem, setScrollItem } = useLiveArticleScrollContext();

  const liveContent = useFragment(
    graphql`
      fragment itemLiveArticleTimelineLiveContent on LiveContent {
        entityUuid
        customTime
        entityUuid
        title
      }
    `,
    reference,
  );

  const isHighlight = useMemo(
    () => notEmpty(liveContent.entityUuid) && liveContent.entityUuid === scrollToTimelineItem,
    [liveContent.entityUuid, scrollToTimelineItem],
  );

  const renderTime = () => {
    if (!notEmpty(liveContent.customTime)) return null;
    return <Time>{formatInTimeZone(liveContent.customTime, "h:mma")}</Time>;
  };

  return (
    <Container
      className={classnames(className, getTimelineItemClassName(liveContent.entityUuid))}
      onClick={() => {
        setScrollItem?.(liveContent?.entityUuid);
        sendGA4Tracking({
          action: "click",
          category: "liveblog",
          customized_parameters: undefined,
          subcategory: "timeline",
        });
      }}
    >
      <Dot />
      {renderTime()}
      <Title $isHighlighted={isHighlight}>
        <span>{liveContent.title}</span>
      </Title>
    </Container>
  );
};
LiveArticleTimelineItem.displayName = "LiveArticleTimelineItem";
